import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import {
  Sparkles,
  MapPin,
  Share2,
  Calendar,
  Pencil,
  Navigation,
  Wallet,
  UserPlus,
} from "lucide-react";
import { Button, Badge, cn } from "@tabipla/ui";
import { getStatusBadgeStyle } from "@/utils/trip-detail";
import type { TripDetailHeaderProps } from "./types";

// Lazy load heavy components
const DownloadPdfButton = lazy(() =>
  import("../pdf").then((m) => ({ default: m.DownloadPdfButton }))
);

export function TripDetailHeader({
  trip,
  coverEmoji,
  coverColor,
  hasItinerary,
  packingItems,
  tripMode,
  daysUntilStart,
  isGenerating,
  isCreatingGroup,
  onEditCover,
  onEditTrip,
  onInvite,
  onShare,
  onGenerate,
}: TripDetailHeaderProps) {
  const { t } = useTranslation();

  return (
    <>
      {/* Compact Header Section */}
      <div className="-mx-4 -mt-4 lg:-mx-6 lg:-mt-6">
        <div
          className={cn(
            "relative flex min-h-[120px] items-end bg-gradient-to-br p-4 sm:min-h-[140px] sm:p-6",
            coverColor
          )}
        >
          {/* Emoji + Title Row */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <button
              onClick={onEditCover}
              className="group relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-3xl backdrop-blur-sm transition-all hover:bg-white/30 sm:h-16 sm:w-16 sm:text-4xl"
              title={t("trips.cover.edit", "カバーを編集")}
            >
              {coverEmoji}
              <div className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-gray-600 shadow-sm transition-all group-hover:scale-110">
                <Pencil className="h-2.5 w-2.5" />
              </div>
            </button>
            <div className="min-w-0 flex-1">
              {/* Title row - タイトルと編集ボタンのみ */}
              <div className="mb-1 flex items-center gap-2">
                <h1 className="min-w-0 flex-1 text-xl font-bold leading-tight text-white sm:text-2xl">
                  {trip.title}
                </h1>
                <button
                  onClick={onEditTrip}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/20 text-white transition-all hover:bg-white/30"
                  title={t("trips.edit", "旅行を編集")}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
              {/* Badges row - ステータスとモードバッジ */}
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <Badge className={cn("text-xs", getStatusBadgeStyle(trip.status))}>
                  {t(`trips.status${trip.status.charAt(0).toUpperCase() + trip.status.slice(1)}`)}
                </Badge>
                {tripMode === "traveling" && (
                  <Badge className="animate-pulse bg-green-500 text-xs text-white">
                    <Navigation className="mr-1 h-3 w-3" />
                    {t("trips.mode.onTrip")}
                  </Badge>
                )}
                {tripMode === "planning" &&
                  daysUntilStart !== null &&
                  daysUntilStart > 0 &&
                  daysUntilStart <= 7 && (
                    <Badge className="bg-blue-500 text-xs text-white">
                      {t("trips.mode.daysUntil", { days: daysUntilStart })}
                    </Badge>
                  )}
                {tripMode === "settlement" && (
                  <Badge className="bg-orange-500 text-xs text-white">
                    <Wallet className="mr-1 h-3 w-3" />
                    {t("trips.mode.settlement")}
                  </Badge>
                )}
              </div>
              <button
                onClick={onEditTrip}
                className="group flex flex-wrap items-center gap-2 text-left text-sm text-white/90 transition-colors hover:text-white sm:gap-3"
              >
                <span className="flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  <span className="truncate">{trip.destination}</span>
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {trip.startDate && trip.endDate
                    ? `${new Date(trip.startDate).toLocaleDateString("ja-JP", { month: "short", day: "numeric" })} - ${new Date(trip.endDate).toLocaleDateString("ja-JP", { month: "short", day: "numeric" })}`
                    : t("trips.dateUndecided", "日程未定")}
                </span>
                <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100" />
              </button>
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden shrink-0 gap-2 sm:flex">
            {hasItinerary && (
              <Suspense fallback={null}>
                <DownloadPdfButton
                  trip={trip}
                  packingItems={packingItems}
                  variant="outline"
                  size="sm"
                  className="border-white/30 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
                />
              </Suspense>
            )}
            <Button
              variant="outline"
              size="sm"
              className="border-white/30 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
              onClick={onInvite}
              disabled={isCreatingGroup}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              {isCreatingGroup
                ? t("trips.creatingGroup", "準備中...")
                : t("trips.inviteMembers", "仲間を招待")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="border-white/30 bg-white/10 text-white backdrop-blur-sm hover:bg-white/20"
              onClick={onShare}
            >
              <Share2 className="mr-2 h-4 w-4" />
              {t("share.title")}
            </Button>
            <Button
              size="sm"
              className="bg-white text-gray-900 hover:bg-white/90"
              onClick={onGenerate}
              disabled={isGenerating}
            >
              <Sparkles className="mr-2 h-4 w-4" />
              {isGenerating ? t("trips.generating") : t("trips.generate")}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Action Buttons */}
      <div className="flex flex-wrap gap-2 sm:hidden">
        {hasItinerary && (
          <Suspense fallback={null}>
            <DownloadPdfButton
              trip={trip}
              packingItems={packingItems}
              variant="outline"
              size="sm"
              className="flex-1"
            />
          </Suspense>
        )}
        <Button
          variant="outline"
          size="sm"
          className="flex-1"
          onClick={onInvite}
          disabled={isCreatingGroup}
        >
          <UserPlus className="mr-2 h-4 w-4" />
          {isCreatingGroup
            ? t("trips.creatingGroup", "準備中...")
            : t("trips.inviteMembers", "仲間を招待")}
        </Button>
        <Button variant="outline" size="sm" className="flex-1" onClick={onShare}>
          <Share2 className="mr-2 h-4 w-4" />
          {t("share.title")}
        </Button>
        <Button
          size="sm"
          className="flex-1 basis-full"
          onClick={onGenerate}
          disabled={isGenerating}
        >
          <Sparkles className="mr-2 h-4 w-4" />
          {isGenerating ? t("trips.generating") : t("trips.generate")}
        </Button>
      </div>
    </>
  );
}
